import { createRouter, createWebHistory } from 'vue-router';
import cookieService from '@/services/cookieService';

// Authentication
import SignIn from '@/views/pages/pages/authentication/login/sign-in.vue'
import Portal from '@/views/pages/pages/Portal.vue'
import ChangePassword from '@/views/pages/pages/ChangePassword.vue'
import AdminPanel from '@/views/pages/pages/AdminPanel.vue'

// Dashboard
import Dashboard from '@/views/pages/dashboard/pos-dashboard.vue'
import AdminDashboard from '@/views/pages/dashboard/admin-dashboard.vue'
import MapMobile from '@/views/pages/dashboard/map-mobile.vue'

// Error Pages
import Error from '@/views/pages/pages/error/pos-error.vue'
import Error_404 from '@/views/pages/pages/error/error-404.vue'
import Error_500 from '@/views/pages/pages/error/error-500.vue'

// Inventory - SSP
import Inventory from '@/views/pages/inventory/pos-inventory.vue'
import Product_List from '@/views/pages/inventory/product-list/product-list.vue'
import Source_List from '@/views/pages/inventory/source-list.vue'

// Purchases/Orders - SSP
import Purchases from '@/views/pages/purchases/pos-purchases.vue'
import PurchaseList from '@/views/pages/purchases/purchase-list.vue'
import AddOrder from '@/views/pages/purchases/add-order.vue'
import OrderDetails from '@/views/pages/purchases/order-details.vue'
import OrderList from '@/views/pages/purchases/order-list.vue'
import Kios_PO_Summary from '@/views/pages/purchases/kios-po-summary.vue'
import PurchaseCalendarMobile from '@/views/pages/purchases/purchase-calendar-mobile.vue'

// People - SSP
import People from '@/views/pages/people/pos-people.vue'
import Pendamping_List from '@/views/pages/people/pendamping-list.vue'
import Kios_List from '@/views/pages/people/kios-list.vue'
import Kios_Details from '@/views/pages/people/kios-details.vue'
import Add_Member from '@/views/pages/people/add-member.vue'
import Edit_Member from '@/views/pages/people/edit-member.vue'

// Users - Admin
import Users from '@/views/pages/users/pos-users.vue'
import Users_List from '@/views/pages/users/users-list.vue'

// Pages wrapper
import Pages from '@/views/pages/pages/pos-pages.vue'

const routes = [
  // ========== PUBLIC ROUTES ==========
  {
    path: '/',
    name: 'portal',
    component: Portal
  },
  {
    path: '/login',
    name: 'sign-in',
    component: SignIn
  },

  // ========== AUTHENTICATED ROUTES ==========
  {
    path: '/admin-management',
    name: 'admin-management',
    component: AdminPanel,
    meta: { permission: { role: 'admin' } }
  },
  {
    path: '/change-password',
    name: 'change-password',
    component: ChangePassword
  },

  // ========== ERROR PAGES ==========
  {
    path: '/error',
    component: Error,
    children: [
      { path: '', redirect: '/error/error-404' },
      { path: "error-404", component: Error_404 },
      { path: "error-500", component: Error_500 },
    ]
  },

  // ========== DASHBOARD ==========
  {
    path: '/dashboard',
    component: Dashboard,
    children: [
      { path: '', redirect: '/dashboard/admin-dashboard' },
      { path: "admin-dashboard", component: AdminDashboard },
      { path: "map-mobile", component: MapMobile },
    ]
  },

  // ========== INVENTORY - SSP ==========
  {
    path: '/inventory',
    component: Inventory,
    children: [
      { path: '', redirect: '/inventory/product-list' },
      { path: "product-list", component: Product_List, meta: { permission: { app: 'ssp', module: 'products' } } },
      { path: "source-list", component: Source_List, meta: { permission: { app: 'ssp', module: 'sources' } } },
    ]
  },

  // ========== PURCHASES/ORDERS - SSP ==========
  {
    path: '/purchases',
    component: Purchases,
    children: [
      { path: '', redirect: '/purchases/order-list' },
      { path: "purchase-list", component: PurchaseList },
      { path: "add-order", component: AddOrder, meta: { permission: { app: 'ssp', module: 'orders', action: 'create' } } },
      { path: "order-details/:id", component: OrderDetails, meta: { permission: { app: 'ssp', module: 'orders' } } },
      { path: "order-list", component: OrderList, meta: { permission: { app: 'ssp', module: 'orders' } } },
      { path: "kios-po-summary", component: Kios_PO_Summary, meta: { permission: { app: 'ssp', module: 'orders' } } },
      { path: "order-calendar", component: PurchaseCalendarMobile, meta: { permission: { app: 'ssp', module: 'orders' } } },
    ]
  },

  // ========== PEOPLE - SSP ==========
  {
    path: '/people',
    component: People,
    children: [
      { path: '', redirect: '/people/kios-list' },
      { path: "pendamping-list", component: Pendamping_List, meta: { permission: { app: 'ssp', module: 'pendampings' } } },
      { path: "kios-list", component: Kios_List, meta: { permission: { app: 'ssp', module: 'members' } } },
      { path: "kios-details/:id", component: Kios_Details, meta: { permission: { app: 'ssp', module: 'members' } } },
      { path: "add-member", component: Add_Member, meta: { permission: { app: 'ssp', module: 'members', action: 'create' } } },
      { path: "edit-member/:id", component: Edit_Member, meta: { permission: { app: 'ssp', module: 'members', action: 'edit' } } },
    ]
  },

  // ========== USERS - ADMIN ==========
  {
    path: '/users',
    component: Users,
    children: [
      { path: '', redirect: '/users/users-list' },
      { path: "users-list", component: Users_List },
    ]
  },

  // ========== CATCH ALL - 404 ==========
  {
    path: '/:pathMatch(.*)*',
    redirect: '/error/error-404'
  }
];

export const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  linkActiveClass: 'active',
  routes,
});

import permissionService from "@/services/permissionService";

router.beforeEach((to, from, next) => {
  // Scroll to the top of the page
  window.scrollTo({ top: 0, behavior: 'smooth' });

  // 1. Check Auth (redirect to login if not authenticated and not on login page)
  const token = cookieService.getCookie('token');
  const userStr = localStorage.getItem('currentUser');
  const user = userStr ? JSON.parse(userStr) : null;
  const publicPages = ['/', '/login'];
  const authRequired = !publicPages.includes(to.path);

  // If cookie is gone but we still have user data in cache, clear it (session expired)
  if (!token && user) {
    localStorage.removeItem('token');
    localStorage.removeItem('currentUser');
    return next('/login');
  }

  if (authRequired && (!user || !token)) {
    return next('/login');
  }

  // 2. Check Permissions
  if (to.meta.permission) {
    const p = to.meta.permission;

    // Role check (for admin management)
    if (p.role && user.role !== p.role) {
      return next('/'); // Redirect back to portal
    }

    // App permission check
    if (p.app && p.module) {
      const hasAccess = permissionService.hasPermission(p.app, p.module, p.action || 'view');
      if (!hasAccess) {
        return next('/'); // Not permitted, back to portal
      }
    }
  }

  // 3. Dynamic Title Update
  const isPublic = publicPages.includes(to.path);
  if (isPublic) {
    document.title = "Sundava Cloud - Strategic Monitoring Ecosystem";
  } else {
    document.title = "SSP - Sundava Cloud";
  }

  // Continue with the navigation
  next();
});
